/**
 * Tab partitioning for the reviewer's "Testrapporter" list.
 *
 * A report is claimed by a reviewer through `reviewedByUserId`, which is set the moment a
 * reviewer starts the review — there is no separate assignment step. "Tilldelade" therefore
 * means "claimed by another reviewer", while the current user's own claims live under
 * "Mina granskningar" so the two queues never show the same report twice.
 */

/** Reports nobody has picked up yet. */
const AWAITING_STATUSES = new Set(["PENDING_REVIEW"]);

/** Reports where the review has run its course, whatever the outcome. */
const FINISHED_STATUSES = new Set(["APPROVED", "REJECTED", "COMPLETED", "PUBLISHED"]);

export const REVIEWER_TAB_IDS = ["AWAITING", "MINE", "ASSIGNED", "FINISHED"];

export const REVIEWER_TAB_LABELS = {
  AWAITING: "Att granska",
  ASSIGNED: "Pågående",
  MINE: "Mina granskningar",
  FINISHED: "Slutförda",
};

export const isFinishedReport = (report) => FINISHED_STATUSES.has(report?.status);

const isAwaitingReview = (report) =>
  AWAITING_STATUSES.has(report?.status) && !report?.reviewedByUserId;

const isAssignedToOther = (report, currentUserId) =>
  !!report?.reviewedByUserId &&
  report.reviewedByUserId !== currentUserId &&
  !isFinishedReport(report);

const isMine = (report, currentUserId) =>
  !!currentUserId && report?.reviewedByUserId === currentUserId && !isFinishedReport(report);

/**
 * Splits reports into the four reviewer tabs. "Tilldelade" and "Mina granskningar" are
 * disjoint: a report the current user has claimed belongs only to their own queue.
 */
export function partitionReviewerReports(reports = [], currentUserId) {
  return {
    AWAITING: reports.filter(isAwaitingReview),
    ASSIGNED: reports.filter((report) => isAssignedToOther(report, currentUserId)),
    MINE: reports.filter((report) => isMine(report, currentUserId)),
    FINISHED: reports.filter(isFinishedReport),
  };
}
